import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ChevronDown, ChevronRight, Plus, Edit, Check, ArrowUp, ArrowDown, FileText, Clock, Archive, Trash2 } from 'lucide-react';
import { Goal, Matter, Project, Client, Document } from '../../lib/db';
import TaskList from '../tasks/TaskList';
import GoalForm from './GoalForm';
import { db } from '../../lib/db';
import { useDbUser } from '../../hooks/useDbUser';
import { toast } from 'sonner';
import MobileGoalCard from '../mobile/MobileGoalCard';
import DocumentList from '../documents/DocumentList';

interface GoalListProps {
  goals: Goal[];
  matters?: Matter[];
  projects?: Project[];
  clients?: Client[];
  selectedYear: number;
  onGoalCreate: (goal: Partial<Goal>) => Promise<void>;
  onGoalUpdate: (id: string, goal: Partial<Goal>) => Promise<void>;
  onGoalReorder: (startIndex: number, endIndex: number) => Promise<void>;
  onGoalArchive?: (id: string) => Promise<void>;
  onGoalDelete?: (id: string) => Promise<void>;
  onGoalMarkComplete?: (id: string) => Promise<void>;
  onGoalMarkIncomplete?: (id: string) => Promise<void>;
  onTemplateSelect?: () => void;
  isArchiveView?: boolean;
  highlightGoalId?: string;
  highlightTaskId?: string;
  shouldHighlightTask?: boolean;
}

function GoalList({ goals, matters, projects, clients, selectedYear, onGoalCreate, onGoalUpdate, onGoalReorder, onGoalArchive, onGoalDelete, onGoalMarkComplete, onGoalMarkIncomplete, onTemplateSelect, isArchiveView = false, highlightGoalId, highlightTaskId, shouldHighlightTask }: GoalListProps) {
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
  const [showNewGoalForm, setShowNewGoalForm] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState<string | null>(null);
  const [completedGoals, setCompletedGoals] = useState<Set<string>>(new Set());
  const [goalTimes, setGoalTimes] = useState<Record<string, number>>({});
  const [collapseTimers, setCollapseTimers] = useState<Record<string, NodeJS.Timeout>>({});
  const [goalDocuments, setGoalDocuments] = useState<Record<string, Document[]>>({});
  
  // Get database user ID using the hook
  const { dbUserId, loading: dbUserLoading } = useDbUser();

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(collapseTimers).forEach(timer => clearTimeout(timer));
    };
  }, [collapseTimers]);

  // Initialize expanded state for all goals except completed ones
  useEffect(() => {
    if (!dbUserId || dbUserLoading) return;
    
    const initializeExpandedState = async () => {
      const newExpanded = new Set<string>();
      const newCompletedGoals = new Set<string>();
      
      for (const goal of goals) {
        const tasks = await db.tasks.findByGoalId(goal.id, dbUserId);
        const allComplete = tasks && tasks.length > 0 && tasks.every(task => task.status === 'Complete');
        
        if (allComplete) {
          newCompletedGoals.add(goal.id);
          // Don't expand completed goals on initial load
        } else {
          newExpanded.add(goal.id);
          // Load documents for expanded goals
          loadGoalDocuments(goal.id);
        }
      }
      
      setCompletedGoals(newCompletedGoals);
      setExpandedGoals(newExpanded);
    };
    
    initializeExpandedState();
  }, [goals, dbUserId, dbUserLoading]);


  // Auto-expand goal if it needs to be highlighted
  useEffect(() => {
    if (highlightGoalId && goals.some(g => g.id === highlightGoalId) && dbUserId && !dbUserLoading) {
      setExpandedGoals(prev => new Set([...prev, highlightGoalId]));
      // Load documents for the highlighted goal
      loadGoalDocuments(highlightGoalId);
    }
  }, [highlightGoalId, goals, dbUserId, dbUserLoading]);

  // Function to update the time tracking display for a specific deliverable
  // Called when time entries are added/updated for tasks within the deliverable
  const refreshGoalTime = async (goalId: string) => {
    if (!dbUserId || dbUserLoading) return;
    
    // Get total time from all time entries for tasks in this deliverable
    const totalSeconds = await db.timeEntries.getTotalTimeByGoalId(goalId, dbUserId);
    setGoalTimes(prev => ({
      ...prev,
      [goalId]: totalSeconds
    }));
  };

  // Function to load documents for a specific goal
  const loadGoalDocuments = async (goalId: string) => {
    if (!dbUserId || dbUserLoading) return;
    
    try {
      // Get all documents and associations
      const [allDocuments, associations] = await Promise.all([
        db.documents.findByUserId(dbUserId),
        db.documentAssociations.findByUserId(dbUserId)
      ]);
      
      // Filter documents associated with this goal/deliverable
      const goalDocs = allDocuments.filter(doc => {
        const docAssociations = associations.filter(a => a.document_id === doc.id);
        return docAssociations.some(a => a.deliverable_id === goalId);
      });
      
      setGoalDocuments(prev => ({
        ...prev,
        [goalId]: goalDocs
      }));
    } catch (error) {
      console.error('Error loading documents for goal:', error);
    }
  };

  // Check for completed goals and fetch total times
  useEffect(() => {
    const checkCompletedGoalsAndTimes = async () => {
      if (!dbUserId || dbUserLoading) return;
      
      const newCompletedGoals = new Set<string>();
      const newGoalTimes: Record<string, number> = {};
      let hasShownNetworkError = false;

      for (const goal of goals) {
        try {
          const tasks = await db.tasks.findByGoalId(goal.id, dbUserId);

          if (tasks && tasks.length > 0) {
            const allComplete = tasks.every(task => task.status === 'Complete');
            if (allComplete) {
              newCompletedGoals.add(goal.id);
            }
          }
          
          // Fetch total time for each goal
          const totalSeconds = await db.timeEntries.getTotalTimeByGoalId(goal.id, dbUserId);
          newGoalTimes[goal.id] = totalSeconds;
        } catch (error) {
          console.error(`Error fetching data for goal ${goal.id}:`, error);
          // Set default value of 0 for goals with errors
          newGoalTimes[goal.id] = 0;
          
          // Show toast notification for network errors (only once)
          if (!hasShownNetworkError && error instanceof Error && error.message.includes('NetworkError')) {
            toast.error('Connection issue: Unable to fetch time tracking data. Please check your internet connection.');
            hasShownNetworkError = true;
          }
        }
      }

      // Check if any goals just became completed
      completedGoals.forEach(goalId => {
        if (!newCompletedGoals.has(goalId)) {
          // This goal is no longer completed, clear any collapse timer
          if (collapseTimers[goalId]) {
            clearTimeout(collapseTimers[goalId]);
            setCollapseTimers(prev => {
              const updated = { ...prev };
              delete updated[goalId];
              return updated;
            });
          }
        }
      });
      
      newCompletedGoals.forEach(goalId => {
        if (!completedGoals.has(goalId) && expandedGoals.has(goalId)) {
          // This goal just became completed and is expanded, set timer to collapse
          const timer = setTimeout(() => {
            setExpandedGoals(prev => {
              const updated = new Set(prev);
              updated.delete(goalId);
              return updated;
            });
            setCollapseTimers(prev => {
              const updated = { ...prev };
              delete updated[goalId];
              return updated;
            });
          }, 10000); // 10 seconds
          
          setCollapseTimers(prev => ({ ...prev, [goalId]: timer }));
        }
      });
      
      setCompletedGoals(newCompletedGoals);
      setGoalTimes(newGoalTimes);
    };

    checkCompletedGoalsAndTimes();
  }, [goals, dbUserId, dbUserLoading]);

  const toggleGoal = (goalId: string) => {
    const newExpanded = new Set(expandedGoals);
    if (newExpanded.has(goalId)) {
      newExpanded.delete(goalId);
    } else {
      newExpanded.add(goalId);
      // Load documents when expanding a goal
      loadGoalDocuments(goalId);
    }
    setExpandedGoals(newExpanded);
  };

  const handleDragEnd = (result: { source?: { index: number }, destination?: { index: number } }) => {
    if (!result.destination) return;
    onGoalReorder(result.source.index, result.destination.index);
  };

  const moveGoal = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < goals.length) {
      await onGoalReorder(index, newIndex);
    }
  };

  const formatTime = (totalSeconds: number): string => {
    if (totalSeconds === 0) return '';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    
    if (hours === 0 && minutes === 0) return '< 1m';
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Deliverables for {selectedYear}</h2>
        <div className="flex gap-2">
          {onTemplateSelect && (
            <button
              onClick={onTemplateSelect}
              className="flex items-center px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <FileText className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">From Template</span>
              <span className="sm:hidden">Template</span>
            </button>
          )}
          <button
            onClick={() => setShowNewGoalForm(true)}
            className="flex items-center px-3 sm:px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">New Deliverable</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      {showNewGoalForm && (
        <GoalForm
          matters={matters}
          projects={projects}
          clients={clients}
          onSubmit={async (goal) => {
            await onGoalCreate({ ...goal, year: selectedYear });
            setShowNewGoalForm(false);
          }}
          onCancel={() => setShowNewGoalForm(false)}
        />
      )}

      {/* Desktop View */}
      <div className="hidden md:block">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="goals">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                {goals.map((goal, index) => (
                  <Draggable key={goal.id} draggableId={goal.id} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="bg-white rounded-lg shadow"
                      >
                      <div
                        {...provided.dragHandleProps}
                        className="p-4 cursor-pointer hover:bg-gray-50"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => toggleGoal(goal.id)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              {expandedGoals.has(goal.id) ? (
                                <ChevronDown className="w-5 h-5" />
                              ) : (
                                <ChevronRight className="w-5 h-5" />
                              )}
                            </button>
                            <div className="flex items-center space-x-2">
                              <h3 className="text-lg font-medium text-gray-900">
                                {goal.title}
                              </h3>
                              {(completedGoals.has(goal.id) || goal.is_complete) && (
                                <Check className="w-5 h-5 text-green-500" />
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <span className="px-2 py-1 text-sm text-gray-600 bg-gray-100 rounded">
                                {goal.category || 'Uncategorized'}
                              </span>
                              <span className="px-2 py-1 text-sm text-gray-600 bg-gray-100 rounded">
                                Priority: {goal.priority}
                              </span>
                              {goal.project_id && projects && (
                                <span className="px-2 py-1 text-sm text-indigo-700 bg-indigo-50 rounded">
                                  {projects.find(p => p.id === goal.project_id)?.title || 'Unknown Project'}
                                </span>
                              )}
                              {goal.matter_id && matters && (
                                <span className="px-2 py-1 text-sm text-purple-700 bg-purple-50 rounded">
                                  {matters.find(m => m.id === goal.matter_id)?.title || 'Unknown Matter'}
                                </span>
                              )}
                              {goalTimes[goal.id] > 0 && (
                                <span className="flex items-center px-2 py-1 text-sm text-blue-700 bg-blue-50 rounded">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {formatTime(goalTimes[goal.id])}
                                </span>
                              )}
                              {goal.start_date && (
                                <span className="px-2 py-1 text-sm text-green-700 bg-green-50 rounded">
                                  Start: {formatDate(goal.start_date)}
                                </span>
                              )}
                              {goal.deadline && (
                                <span className="px-2 py-1 text-sm text-red-700 bg-red-50 rounded">
                                  Due: {formatDate(goal.deadline)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => moveGoal(index, 'up')}
                                disabled={index === 0}
                                className={`p-1.5 rounded-full ${
                                  index === 0
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : 'text-gray-400 hover:text-indigo-600 hover:bg-gray-100'
                                }`}
                                title="Move Up"
                              >
                                <ArrowUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => moveGoal(index, 'down')}
                                disabled={index === goals.length - 1}
                                className={`p-1.5 rounded-full ${
                                  index === goals.length - 1
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : 'text-gray-400 hover:text-indigo-600 hover:bg-gray-100'
                                }`}
                                title="Move Down"
                              >
                                <ArrowDown className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingGoalId(goal.id);
                                }}
                                className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-full hover:bg-gray-100"
                                title="Edit Deliverable"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              {completedGoals.has(goal.id) && !goal.is_complete && onGoalMarkComplete && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await onGoalMarkComplete(goal.id);
                                    toast.success('Deliverable marked as complete');
                                  }}
                                  className="p-1.5 text-gray-400 hover:text-green-600 rounded-full hover:bg-gray-100"
                                  title="Mark Deliverable Complete"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                              )}
                              {goal.is_complete && onGoalMarkIncomplete && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await onGoalMarkIncomplete(goal.id);
                                    toast.success('Deliverable marked as incomplete');
                                  }}
                                  className="p-1.5 text-green-600 hover:text-gray-600 rounded-full hover:bg-green-50"
                                  title="Mark Deliverable Incomplete"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                              )}
                              {onGoalArchive && (isArchiveView || completedGoals.has(goal.id)) && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await onGoalArchive(goal.id);
                                    toast.success(isArchiveView ? 'Deliverable restored' : 'Deliverable archived');
                                  }}
                                  className="p-1.5 text-gray-400 hover:text-amber-600 rounded-full hover:bg-gray-100"
                                  title={isArchiveView ? "Restore Deliverable" : "Archive Deliverable"}
                                >
                                  <Archive className="w-4 h-4" />
                                </button>
                              )}
                              {onGoalDelete && isArchiveView && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (window.confirm('Are you sure you want to permanently delete this deliverable? This action cannot be undone.')) {
                                      await onGoalDelete(goal.id);
                                    }
                                  }}
                                  className="p-1.5 text-gray-400 hover:text-red-600 rounded-full hover:bg-gray-100"
                                  title="Delete Deliverable Permanently"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {editingGoalId === goal.id && (
                        <div className="border-t border-gray-200 p-4">
                          <GoalForm
                            goal={goal}
                            matters={matters}
                            projects={projects}
                            clients={clients}
                            onSubmit={async (updates) => {
                              await onGoalUpdate(goal.id, updates);
                              setEditingGoalId(null);
                            }}
                            onCancel={() => setEditingGoalId(null)}
                          />
                        </div>
                      )}
                      {expandedGoals.has(goal.id) && dbUserId && (
                        <div className="border-t border-gray-200">
                          <div className="p-4">
                            <TaskList 
                              goalId={goal.id}
                              goal={goal}
                              matter={matters?.find(m => m.id === goal.matter_id)}
                              project={projects?.find(p => p.id === matters?.find(m => m.id === goal.matter_id)?.project_id)}
                              client={(() => {
                                // Find client from goal, matter, or project
                                if (goal.client_id) return clients?.find(c => c.id === goal.client_id);
                                const matter = matters?.find(m => m.id === goal.matter_id);
                                if (matter?.client_id) return clients?.find(c => c.id === matter.client_id);
                                const project = projects?.find(p => p.id === matter?.project_id);
                                if (project?.client_id) return clients?.find(c => c.id === project.client_id);
                                return undefined;
                              })()}
                              dbUserId={dbUserId} 
                              onTimeEntryUpdate={() => refreshGoalTime(goal.id)}
                              onTaskUpdate={async () => {
                                // Check if all tasks are completed after an update
                                const tasks = await db.tasks.findByGoalId(goal.id, dbUserId);
                                const allComplete = tasks && tasks.length > 0 && tasks.every(task => task.status === 'Complete');
                                
                                if (allComplete && !completedGoals.has(goal.id)) {
                                  // This goal just became completed, update state and set timer
                                  setCompletedGoals(prev => new Set([...prev, goal.id]));
                                  
                                  if (expandedGoals.has(goal.id)) {
                                    const timer = setTimeout(() => {
                                      setExpandedGoals(prev => {
                                        const updated = new Set(prev);
                                        updated.delete(goal.id);
                                        return updated;
                                      });
                                      setCollapseTimers(prev => {
                                        const updated = { ...prev };
                                        delete updated[goal.id];
                                        return updated;
                                      });
                                    }, 10000); // 10 seconds
                                    
                                    setCollapseTimers(prev => ({ ...prev, [goal.id]: timer }));
                                  }
                                } else if (!allComplete && completedGoals.has(goal.id)) {
                                  // This goal is no longer completed
                                  setCompletedGoals(prev => {
                                    const updated = new Set(prev);
                                    updated.delete(goal.id);
                                    return updated;
                                  });
                                  
                                  // Clear any collapse timer
                                  if (collapseTimers[goal.id]) {
                                    clearTimeout(collapseTimers[goal.id]);
                                    setCollapseTimers(prev => {
                                      const updated = { ...prev };
                                      delete updated[goal.id];
                                      return updated;
                                    });
                                  }
                                }
                              }}
                              highlightTaskId={highlightGoalId === goal.id ? highlightTaskId : undefined}
                              shouldHighlightTask={highlightGoalId === goal.id ? shouldHighlightTask : undefined}
                            />
                          </div>
                          {/* Documents Section */}
                          {goalDocuments[goal.id] && goalDocuments[goal.id].length > 0 && (
                            <div className="border-t border-gray-200 p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-medium text-gray-900">Documents</h4>
                                <div className="flex items-center text-xs text-gray-500">
                                  <FileText className="w-3 h-3 mr-1" />
                                  <span>{goalDocuments[goal.id].length} document{goalDocuments[goal.id].length !== 1 ? 's' : ''}</span>
                                </div>
                              </div>
                              <DocumentList
                                documents={goalDocuments[goal.id]}
                                dbUserId={dbUserId}
                                showFolders={false}
                                compact={true}
                                onDocumentDeleted={() => loadGoalDocuments(goal.id)}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      </div>

      {/* Mobile View */}
      <div className="md:hidden space-y-3">
        {goals.map((goal) => (
          <MobileGoalCard
            key={goal.id}
            goal={goal}
            matters={matters}
            projects={projects}
            isCompleted={completedGoals.has(goal.id)}
            isExpanded={expandedGoals.has(goal.id)}
            totalTime={goalTimes[goal.id] || 0}
            onToggle={() => toggleGoal(goal.id)}
            onEdit={() => setEditingGoalId(goal.id)}
            onArchive={onGoalArchive ? async () => {
              await onGoalArchive(goal.id);
              toast.success(isArchiveView ? 'Deliverable restored' : 'Deliverable archived');
            } : undefined}
            onDelete={onGoalDelete && isArchiveView ? async () => {
              if (window.confirm('Are you sure you want to permanently delete this deliverable? This action cannot be undone.')) {
                await onGoalDelete(goal.id);
              }
            } : undefined}
            onMarkComplete={onGoalMarkComplete ? async () => {
              await onGoalMarkComplete(goal.id);
              toast.success('Deliverable marked as complete');
            } : undefined}
            onMarkIncomplete={onGoalMarkIncomplete ? async () => {
              await onGoalMarkIncomplete(goal.id);
              toast.success('Deliverable marked as incomplete');
            } : undefined}
            isArchiveView={isArchiveView}
            renderTaskList={() => (
              <>
                {editingGoalId === goal.id && (
                  <div className="p-4 bg-white">
                    <GoalForm
                      goal={goal}
                      matters={matters}
                      projects={projects}
                      clients={clients}
                      onSubmit={async (updates) => {
                        await onGoalUpdate(goal.id, updates);
                        setEditingGoalId(null);
                      }}
                      onCancel={() => setEditingGoalId(null)}
                    />
                  </div>
                )}
                {editingGoalId !== goal.id && dbUserId && (
                  <div className="p-4">
                    <TaskList 
                      goalId={goal.id}
                      goal={goal}
                      matter={matters?.find(m => m.id === goal.matter_id)}
                      project={projects?.find(p => p.id === matters?.find(m => m.id === goal.matter_id)?.project_id)}
                      client={(() => {
                        // Find client from goal, matter, or project
                        if (goal.client_id) return clients?.find(c => c.id === goal.client_id);
                        const matter = matters?.find(m => m.id === goal.matter_id);
                        if (matter?.client_id) return clients?.find(c => c.id === matter.client_id);
                        const project = projects?.find(p => p.id === matter?.project_id);
                        if (project?.client_id) return clients?.find(c => c.id === project.client_id);
                        return undefined;
                      })()}
                      dbUserId={dbUserId} 
                      onTimeEntryUpdate={() => refreshGoalTime(goal.id)}
                      onTaskUpdate={async () => {
                        // Check if all tasks are completed after an update
                        const tasks = await db.tasks.findByGoalId(goal.id, dbUserId);
                        const allComplete = tasks && tasks.length > 0 && tasks.every(task => task.status === 'Complete');
                        
                        if (allComplete && !completedGoals.has(goal.id)) {
                          // This goal just became completed, update state and set timer
                          setCompletedGoals(prev => new Set([...prev, goal.id]));
                          
                          if (expandedGoals.has(goal.id)) {
                            const timer = setTimeout(() => {
                              setExpandedGoals(prev => {
                                const updated = new Set(prev);
                                updated.delete(goal.id);
                                return updated;
                              });
                              setCollapseTimers(prev => {
                                const updated = { ...prev };
                                delete updated[goal.id];
                                return updated;
                              });
                            }, 10000); // 10 seconds
                            
                            setCollapseTimers(prev => ({ ...prev, [goal.id]: timer }));
                          }
                        } else if (!allComplete && completedGoals.has(goal.id)) {
                          // This goal is no longer completed
                          setCompletedGoals(prev => {
                            const updated = new Set(prev);
                            updated.delete(goal.id);
                            return updated;
                          });
                          
                          // Clear any collapse timer
                          if (collapseTimers[goal.id]) {
                            clearTimeout(collapseTimers[goal.id]);
                            setCollapseTimers(prev => {
                              const updated = { ...prev };
                              delete updated[goal.id];
                              return updated;
                            });
                          }
                        }
                      }}
                      highlightTaskId={highlightGoalId === goal.id ? highlightTaskId : undefined}
                      shouldHighlightTask={highlightGoalId === goal.id ? shouldHighlightTask : undefined}
                    />
                    {/* Mobile Documents Section */}
                    {goalDocuments[goal.id] && goalDocuments[goal.id].length > 0 && (
                      <div className="border-t border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-900">Documents</h4>
                          <div className="flex items-center text-xs text-gray-500">
                            <FileText className="w-3 h-3 mr-1" />
                            <span>{goalDocuments[goal.id].length} document{goalDocuments[goal.id].length !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                        <DocumentList
                          documents={goalDocuments[goal.id]}
                          dbUserId={dbUserId}
                          showFolders={false}
                          compact={true}
                          onDocumentDeleted={() => loadGoalDocuments(goal.id)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          />
        ))}
      </div>
    </div>
  );
}

export default GoalList;